<template>
	<v-card
		class="kanban-card mb-2"
		:class="{
			'kanban-card--draggable': isDraggable,
			'kanban-card--readonly': !isDraggable,
			'kanban-card--ended': item.task.status === 3,
			'kanban-card--backlog': isBacklog
		}"
		@click.stop="$emit('open-task', item)"
	>
		<v-card-text class="pa-2">
			<div class="d-flex flex-row align-start">
				<!-- Iconos de estado (número + prioridad) -->
				<div class="d-flex flex-column mr-2" style="min-width:24px">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<div class="kcard-badge number-badge mb-1" v-bind="attrs" v-on="on">
								<span class="white--text" style="font-size:9px;line-height:1;">{{ item.task.number }}</span>
							</div>
						</template>
						<span>Tarea #{{ item.task.number }}</span>
					</v-tooltip>
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<div class="kcard-badge mb-1"
								:class="priorityColorClass"
								v-bind="attrs"
								v-on="on">
								<v-icon color="white" style="font-size:12px;">mdi-exclamation</v-icon>
							</div>
						</template>
						<span>Prioridad {{ priorityText }}</span>
					</v-tooltip>
					<v-tooltip bottom v-if="item.task.status === 3">
						<template v-slot:activator="{ on, attrs }">
							<v-icon color="#34BFA3" small v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
						</template>
						<span>Tarea Finalizada</span>
					</v-tooltip>
					<v-tooltip bottom v-if="isNearDeadline">
						<template v-slot:activator="{ on, attrs }">
							<v-icon color="#fb8c00" small v-bind="attrs" v-on="on" class="mt-1">mdi-flag</v-icon>
						</template>
						<span>Fecha crítica próxima: {{ item.task.critical_date | toDate }}</span>
					</v-tooltip>
				</div>

				<!-- Contenido principal -->
				<div class="flex-grow-1 overflow-hidden">
					<p class="mb-1 font-weight-medium" style="font-size:12px;line-height:1.3;word-break:break-word;">
						{{ item.task.title }}
					</p>
					<div class="kcard-meta">
						<v-icon style="font-size:10px;" color="#5e85ef">mdi-account-outline</v-icon>
						<span class="kcard-meta-text">{{ item.task.created_by_user ? item.task.created_by_user.name : '—' }}</span>
					</div>
					<div class="kcard-meta">
						<v-icon style="font-size:10px;" color="#5e85ef">mdi-account-check-outline</v-icon>
						<span class="kcard-meta-text">{{ item.task.responsable_user ? item.task.responsable_user.name : '—' }}</span>
					</div>
					<div class="kcard-meta mt-1" v-if="item.task.critical_date">
						<v-icon style="font-size:10px;" color="#5E85EF">mdi-calendar-range-outline</v-icon>
						<span class="kcard-meta-text light-blue-text">{{ item.task.critical_date | toDate }}</span>
					</div>
					<div class="kcard-meta" v-else-if="item.task.created_at">
						<v-icon style="font-size:10px;" color="grey">mdi-calendar-range-outline</v-icon>
						<span class="kcard-meta-text grey-text">{{ item.task.created_at | toDate }}</span>
					</div>
				</div>
			</div>

			<!-- Indicador "no puedes mover" -->
			<v-tooltip bottom v-if="!isDraggable">
				<template v-slot:activator="{ on, attrs }">
					<div class="readonly-overlay" v-bind="attrs" v-on="on">
						<v-icon x-small color="grey lighten-1">mdi-lock-outline</v-icon>
					</div>
				</template>
				<span>No eres creador ni responsable de esta tarea</span>
			</v-tooltip>
		</v-card-text>
	</v-card>
</template>

<script>
import moment from 'moment'

export default {
	name: 'KanbanCard',
	props: {
		item: {
			type: Object,
			required: true
		},
		isDraggable: {
			type: Boolean,
			default: true
		},
		isBacklog: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		priorityColorClass() {
			if (this.item.task.priority === 3) return 'high'
			if (this.item.task.priority === 2) return 'medium'
			return 'normal'
		},
		priorityText() {
			if (this.item.task.priority === 3) return 'Inmediata'
			if (this.item.task.priority === 2) return 'Media'
			return 'Estándar'
		},
		isNearDeadline() {
			if (!this.item.task.critical_date) return false
			const now = moment()
			const d = moment(this.item.task.critical_date)
			return d.diff(now, 'days') <= 2
		}
	},
	emits: ['open-task']
}
</script>

<style scoped>
.kanban-card {
	position: relative;
	border-left: 3px solid #5e85ef;
	transition: box-shadow 0.15s ease, opacity 0.15s ease;
	cursor: grab;
}
.kanban-card--draggable:hover {
	box-shadow: 0 4px 12px rgba(94, 133, 239, 0.25) !important;
}
.kanban-card--readonly {
	cursor: default;
	opacity: 0.65;
	border-left-color: #bdbdbd;
}
.kanban-card--ended {
	border-left-color: #34BFA3;
}
.kanban-card--backlog {
	border-left-color: #9e9e9e;
}
.kcard-badge {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}
.number-badge {
	background-color: #2D4B95;
}
.high { background-color: #F4516C; }
.medium { background-color: #fb8c00; }
.normal { background-color: #3EBFA3; }
.kcard-meta {
	display: flex;
	align-items: center;
	gap: 3px;
}
.kcard-meta-text {
	font-size: 10px;
	color: #5e85ef;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 160px;
}
.grey-text { color: #9e9e9e !important; }
.light-blue-text { color: #5E85EF !important; }
.readonly-overlay {
	position: absolute;
	top: 4px;
	right: 4px;
}
</style>
